import { observer } from 'mobx-react';
import { Component } from 'react';
import CancelRefund from '../CancelRefund/CancelRefund';
import { BUTTON_TITLE, PAYMENT_TYPES } from 'src/constants/main';
import { ORDERS_GROUPS, ORDERS_TYPES_GROUP, ORDER_STATUS, ORDER_TYPE } from 'src/constants/order';
import { localeStore } from 'src/mobx/localesStore';
import { TOrder } from 'src/types/orders';
import { orderStatusManager } from 'src/utils/orderStatusManager';
import Button from 'src/view/components/Controls/ButtonComponent/Button';
import ModalMessage from 'src/view/components/ModalMessage/ModalMessage';
import PopupModal from 'src/view/components/Popup/Popup';
import { OrderStore } from '~/mobx/orderStore';
import logger from '~/services/logger';
import { IMessageModalOption, messagesStore } from '~/view/containers/Messages';
import { withOrderStore } from '~/view/screens/OrderScreen/context';
import './style.scss';
import { TButtonAppearance } from '~/view/components/Controls/ButtonComponent/types';

interface State {
  cancelOrderModal: boolean;
  messageText: string;
  cancelingOrder: boolean;
  currentOrderId: TOrder['id'] | undefined;
}

// @ts-ignore
@withOrderStore
@observer
export default class OrderCancelButton extends Component<{ orderStore?: OrderStore }, State> {
  state = {
    cancelOrderModal: false,
    messageText: '',
    cancelingOrder: true,
    currentOrderId: undefined,
  };

  get orderStore() {
    return this.props.orderStore!;
  }

  openCancelOrderModal = () => {
    this.setState({ cancelOrderModal: true });
  };

  closeCancelOrderModal = () => {
    this.setState({ cancelOrderModal: false });
  };

  cancelOrder = async () => {
    if (!this.orderStore.order || !this.state.cancelingOrder) {
      this.closeCancelOrderModal();
      return;
    }

    if (this.orderStore.order.payment_type === PAYMENT_TYPES.ONLINE_PAYMENT) {
      messagesStore.addMessage({
        id: 'refund-reason-message',
        Content: () => <CancelRefund orderStore={this.orderStore} />,
        contentClassName: 'refund-reason-message',
      });
      return;
    }

    try {
      await this.orderStore.changeStatus(this.orderStore.order.id, ORDER_STATUS.CANCELED);
      this.setState({ currentOrderId: undefined });
      this.closeCancelOrderModal();
    } catch (error: any) {
      this.closeCancelOrderModal();
      logger.error({ error, place: 'OrderCancelButton cancelOrder', showModal: true });
    }
  };

  updateCancelButton = () => {
    const response = orderStatusManager(this.orderStore.order);
    if (!response) return null;
    this.setState({
      messageText: response.cancellingMessageText,
      cancelingOrder: response.cancellingOrder,
      currentOrderId: this.orderStore.order?.id,
    });
  };

  componentDidMount = () => {
    this.updateCancelButton();
  };

  componentDidUpdate = () => {
    if (this.orderStore.order?.id !== this.state.currentOrderId) {
      this.updateCancelButton();
    }
  };

  get isUnavailableCancel(): boolean {
    const { order, loading } = this.orderStore;
    return !order || (!loading && ORDERS_TYPES_GROUP[ORDER_TYPE.HISTORY][ORDERS_GROUPS.CANCELED].includes(order.order_status_code));
  }

  get messageOptions(): IMessageModalOption[] {
    return this.state.cancelingOrder
      ? [
          { title: localeStore.t(BUTTON_TITLE.YES), onPress: this.cancelOrder, loading: this.orderStore.loading },
          { title: localeStore.t(BUTTON_TITLE.NO), onPress: this.closeCancelOrderModal, appearance: 'tertiary' },
        ]
      : [{ title: localeStore.t(BUTTON_TITLE.OK), onPress: this.closeCancelOrderModal, appearance: 'tertiary' }];
  }

  get buttonAppearance(): TButtonAppearance {
    const { order } = this.orderStore;
    return order?.order_status_code === ORDER_STATUS.RETURNED ? 'destructive-tertiary' : 'destructive-primary';
  }

  render() {
    if (this.isUnavailableCancel) return null;

    return (
      <PopupModal
        position="top"
        direction="left"
        justify="end"
        selfClose={false}
        backdrop
        onClose={this.closeCancelOrderModal}
        visible={this.state.cancelOrderModal}
        content={({ closeModal }) => (
          <ModalMessage
            minWidth={300}
            title={localeStore.t(`${this.state.messageText}`)}
            titleClassName="order-cancel__modal-title"
            closeModal={closeModal}
            iconClassName="color-gray2"
            options={this.messageOptions}
          />
        )}
      >
        <Button
          title={localeStore.t('txt_cancel_order')}
          onClick={this.openCancelOrderModal}
          loading={this.orderStore.loading}
          appearance={this.buttonAppearance}
          size="large"
          stretched
        />
      </PopupModal>
    );
  }
}
